import { assign, createMachine } from 'xstate';
import { sendParent } from 'xstate/lib/actions';

import {
  AUTH_CORE_MACHINE_ID,
  authCoreMachineActions,
  authCoreMachineEvents,
  authCoreMachineGuards,
  authCoreMachineServices,
  authCoreMachineStates,
  authCoreMachineTags
} from '../../constants/authCoreMachineConstants';
import {
  AuthCoreMachineContext,
  AuthCoreMachineEvent
} from '../../types/authCoreMachineTypes';

const states = authCoreMachineStates;
const events = authCoreMachineEvents;
const actions = authCoreMachineActions;
const services = authCoreMachineServices;
const tags = authCoreMachineTags;
const guards = authCoreMachineGuards;

const getInitialState = (
  fieldType: string | undefined,
  shouldStartWithInitialState: boolean
) => {
  if (fieldType === 'phone') {
    return shouldStartWithInitialState
      ? states.initialState
      : states.getPhoneNumberOTPState;
  } else if (fieldType === 'email') {
    return shouldStartWithInitialState
      ? states.initialState
      : states.getEmailOtpState;
  }
  return states.getPhoneNumberOTPState;
};

export const authCoreMachine = (
  shouldStartWithInitialState: boolean,
  fieldType: string | undefined,
  fieldValue?: string,
  fieldName?: string
) =>
  createMachine<AuthCoreMachineContext, AuthCoreMachineEvent>({
    id: AUTH_CORE_MACHINE_ID,
    initial: getInitialState(fieldType, shouldStartWithInitialState),
    context: {
      otpFieldRef: null,
      accessToken: '',
      refreshToken: '',
      isOtpSentToUser: false,
      isOtpVerified: false,
      isGetOtpButtonLoading: false
    },
    on: {
      [events.onSuccessOtpVerification]: {
        actions: [actions.setAccessTokenAndRefreshTokenToContext],
        target: states.otpVerificationSuccessState
      },
      [events.onSuccessEmailOtpVerification]: {
        target: states.emailOtpVerificationSuccessState
      },
      [events.onClickResendOtp]: {
        actions: [
          sendParent((context, event) => ({
            type: events.onClickResendOtp
          }))
        ]
      }
    },
    states: {
      [states.initialState]: {
        on: {
          [events.goToGetPhoneNumberOtpState]: {
            target: states.getPhoneNumberOTPState
          },
          [events.goToGetEmailOtpState]: {
            target: states.updateUserEmail
          }
        }
      },
      [states.getPhoneNumberOTPState]: {
        tags: [tags.otpApiInitiated],
        entry: [
          actions.setIsGetOtpButtonLoadingToTrue,
          actions.doActionsFromParentOnEntryGetOTP
        ],
        invoke: {
          src: services.getLoginOtp,
          onDone: {
            actions: [
              actions.setIsGetOtpButtonLoadingToFalse,
              actions.setIsOtpSentToUserTrueInContext,
              actions.spawnExistingUserOTPFieldMachine,
              actions.doActionsFromParentOnSuccessGetOTP
            ]
          },
          onError: [
            {
              cond: guards.isNewUserError,
              target: states.newUserLoginState,
              actions: [actions.onErrorGetLoginOTP]
            },
            {
              cond: guards.isPhoneNumberNotVerifiedError,
              target: states.phoneNumberNotVerifiedState,
              actions: [actions.onErrorGetLoginOTP]
            },
            {
              cond: guards.isInvalidRequestSignatureError,
              actions: [
                actions.onErrorGetLoginOTP,
                actions.onInvalidRequestSignatureError
              ]
            },
            {
              actions: [
                actions.onErrorGetLoginOTP,
                actions.setIsGetOtpButtonLoadingToFalse,
                actions.doActionsFromParentOnErrorGetOTP
              ]
            }
          ]
        },
        on: {
          [events.doSelfTransition]: {
            target: states.getPhoneNumberOTPState
          }
        }
      },
      [states.newUserLoginState]: {
        entry: [
          actions.setIsGetOtpButtonLoadingToTrue,
          actions.doActionsFromParentOnEntryGetOTP
        ],
        invoke: {
          src: services.getSignUpOTP,
          onDone: {
            actions: [
              actions.setIsGetOtpButtonLoadingToFalse,
              actions.setIsOtpSentToUserTrueInContext,
              actions.spawnNewUserOTPFieldMachine,
              actions.doActionsFromParentOnSuccessGetSignUpOTP
            ]
          },
          onError: {
            actions: [
              actions.setIsGetOtpButtonLoadingToFalse,
              actions.doActionsFromParentOnErrorGetOTP
            ]
          }
        },
        on: {
          [events.doSelfTransition]: {
            target: states.newUserLoginState
          }
        }
      },
      [states.phoneNumberNotVerifiedState]: {
        entry: [
          actions.setIsGetOtpButtonLoadingToTrue,
          actions.doActionsFromParentOnEntryGetOTP
        ],
        invoke: {
          src: services.getVerifiedUserOTP,
          onDone: {
            actions: [
              actions.setIsGetOtpButtonLoadingToFalse,
              actions.setIsOtpSentToUserTrueInContext,
              actions.spawnNewUserOTPFieldMachine,
              actions.doActionsFromParentOnSuccessGetSignUpOTP
            ]
          },
          onError: {
            actions: [
              actions.setIsGetOtpButtonLoadingToFalse,
              actions.doActionsFromParentOnErrorGetOTP
            ]
          }
        },
        on: {
          [events.doSelfTransition]: {
            target: states.phoneNumberNotVerifiedState
          }
        }
      },
      [states.emailOtpVerificationSuccessState]: {
        entry: [
          assign((context) => {
            return {
              isOtpVerified: true
            };
          }),
          sendParent((context, event) => ({
            type: events.onSuccessEmailOtpVerification,
            data: {
              ...context,
              fieldDetail: {
                field: fieldName,
                value: fieldValue
              }
            }
          }))
        ]
      },
      [states.otpVerificationSuccessState]: {
        entry: [
          assign((context) => {
            return {
              isOtpVerified: true
            };
          }),
          sendParent((context, event) => ({
            type: events.onSuccessOtpVerification,
            data: {
              ...context,
              fieldDetail: {
                field: fieldName,
                value: fieldValue
              }
            }
          }))
        ]
      },
      [states.updateUserEmail]: {
        entry: [actions.setIsGetOtpButtonLoadingToTrue],
        invoke: {
          src: services.updateUserEmail,
          onDone: {
            target: states.getEmailOtpState
          },
          onError: {
            actions: [
              actions.setIsGetOtpButtonLoadingToFalse,
              actions.doActionsFromParentOnErrorGetEmailOTP
            ]
          }
        },
        on: {
          [events.doSelfTransition]: {
            target: states.updateUserEmail
          }
        }
      },
      [states.getEmailOtpState]: {
        entry: [
          actions.setIsGetOtpButtonLoadingToTrue,
          actions.doActionsFromParentOnEnterGetEmailOTP
        ],
        invoke: {
          src: services.getEmailOtp,
          onDone: {
            actions: [
              actions.setIsGetOtpButtonLoadingToFalse,
              actions.setIsOtpSentToUserTrueInContext,
              actions.spawnExistingUserOTPFieldMachine,
              actions.doActionsFromParentOnSuccessGetEmailOTP
            ]
          },
          onError: [
            {
              actions: [
                actions.setIsGetOtpButtonLoadingToFalse,
                actions.doActionsFromParentOnErrorGetEmailOTP
              ]
            }
          ]
        },
        on: {
          [events.doSelfTransition]: {
            target: states.getEmailOtpState
          }
        }
      }
    }
  });
